import { Controller } from "@hotwired/stimulus";

// Bound to `.lc-training__elements` INSIDE the Turbo Frame, so it gets
// re-instantiated every time the user navigates to a different lesson.
//
// Two responsibilities:
//   1. Drive the per-lesson timer — every 10s we POST the elapsed time
//      onto the LessonProgress, with a `document.hidden` guard so we don't
//      accumulate seconds while the tab is in the background.
//   2. Bubble child events (QCM submitted, depot uploaded, video watched)
//      up to the outer training controller as `lc-training:progress-changed`.
const TICK_INTERVAL_MS = 10_000;

export default class extends Controller {
  static values = {
    progressId: String,
    juniorId:   String,
    userId:     String
  };

  connect() {
    this._lastTick = Date.now();
    this._tickHandler = () => this._tick();
    this._timer = setInterval(this._tickHandler, TICK_INTERVAL_MS);

    // Catch the "tab loses focus" / "tab regains focus" transitions so we
    // never count time spent in another tab.
    this._visibilityHandler = () => { this._lastTick = Date.now(); };
    document.addEventListener("visibilitychange", this._visibilityHandler);

    // Bubble child events up to the outer training controller.
    this._bubble = (event) => this._onChildProgress(event);
    this.element.addEventListener("lc-training-element:progress-changed", this._bubble);

    // Final flush — best-effort POST on tab close so the last few seconds
    // aren't lost.
    this._unloadHandler = () => this._flush();
    window.addEventListener("beforeunload", this._unloadHandler);
  }

  disconnect() {
    if (this._timer) clearInterval(this._timer);
    document.removeEventListener("visibilitychange", this._visibilityHandler);
    this.element.removeEventListener("lc-training-element:progress-changed", this._bubble);
    window.removeEventListener("beforeunload", this._unloadHandler);
    // One last flush when the user navigates away — the disconnect itself
    // happens during Turbo Frame swap, so we want to persist the time
    // accumulated on the lesson we're leaving.
    this._flush();
  }

  async _tick() {
    if (!this._canPersist()) return;
    if (document.hidden) {
      this._lastTick = Date.now();
      return;
    }
    const elapsed = Math.round((Date.now() - this._lastTick) / 1000);
    this._lastTick = Date.now();
    if (elapsed <= 0) return;
    await this._post(elapsed);
  }

  _flush() {
    if (!this._canPersist() || document.hidden) return;
    const elapsed = Math.round((Date.now() - this._lastTick) / 1000);
    this._lastTick = Date.now();
    if (elapsed <= 0) return;
    // Use `keepalive` so the browser still sends the request even though
    // we're in the middle of a navigation/teardown.
    this._post(elapsed, { keepalive: true }).catch(() => { /* silent */ });
  }

  _canPersist() {
    return this.progressIdValue && this.progressIdValue !== "";
  }

  async _post(elapsed, extraOpts = {}) {
    try {
      await fetch(`/juniors/${this.juniorIdValue}/lesson_progresses/${this.progressIdValue}/update_time`, {
        method: "PATCH",
        headers: this._headers(),
        credentials: "same-origin",
        body: JSON.stringify({ time: elapsed }),
        ...extraOpts
      });
    } catch (_e) { /* drop silently */ }
  }

  _onChildProgress(event) {
    const evt = new CustomEvent("lc-training:progress-changed", {
      bubbles: true,
      detail: event.detail
    });
    this.element.dispatchEvent(evt);
  }

  _headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      Accept: "application/json"
    };
  }
}
